import React, { useEffect, useState } from 'react';
import { getDailyAdStats, type DailyAdStat } from '../../services/api';

interface DailyStatsChartProps {
  userId?: string;
  adId?: string;
  metric: 'views' | 'clicks' | 'telemetryBlankEvents';
  from?: string;
  to?: string;
}

export const DailyStatsChart: React.FC<DailyStatsChartProps> = ({
  userId,
  adId,
  metric,
  from,
  to,
}) => {
  const [points, setPoints] = useState<DailyAdStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const stats = await getDailyAdStats({ userId, adId, from, to });
        if (!cancelled) {
          setPoints(stats);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          let msg =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Daily trend data is not available right now.';

          const lower = msg.toLowerCase();
          if (lower.includes('request failed') || lower.includes('network error')) {
            msg =
              'Daily analytics are not available in this environment yet. This is expected if analytics are disabled.';
          }

          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [userId, adId, from, to]);

  if (loading) {
    return <p style={{ fontSize: 12 }}>Loading daily trend…</p>;
  }

  if (error) {
    return <p style={{ fontSize: 12, color: '#4b5563' }}>{error}</p>;
  }

  if (!points.length) {
    return <p style={{ fontSize: 12, opacity: 0.7 }}>No daily stats available yet.</p>;
  }

  const metricKey: keyof DailyAdStat =
    metric === 'views' ? 'views' : metric === 'clicks' ? 'clicks' : 'telemetryBlankEvents';

  const values = points.map((p) => p[metricKey] ?? 0);
  const maxValue = values.reduce((max, v) => (v > max ? v : max), 0) || 1;

  const width = 400;
  const height = 160;
  const paddingLeft = 32;
  const paddingRight = 8;
  const paddingTop = 16;
  const paddingBottom = 24;

  const innerWidth = width - paddingLeft - paddingRight;
  const innerHeight = height - paddingTop - paddingBottom;

  const xForIndex = (index: number) => {
    if (points.length === 1) return paddingLeft + innerWidth / 2;
    const t = index / (points.length - 1);
    return paddingLeft + t * innerWidth;
  };

  const yForValue = (value: number) => {
    if (maxValue === 0) return paddingTop + innerHeight;
    const ratio = value / maxValue;
    return paddingTop + innerHeight - ratio * innerHeight;
  };

  const pathD = points
    .map((p, index) => {
      const x = xForIndex(index);
      const y = yForValue(p[metricKey] ?? 0);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  const metricLabel =
    metric === 'views' ? 'Views' : metric === 'clicks' ? 'Clicks' : 'Safety blanks';

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ marginBottom: 4, fontSize: 12, opacity: 0.8 }}>{metricLabel} per day</div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img">
        <desc>Daily {metricLabel.toLowerCase()} trend</desc>
        {/* Axis */}
        <line
          x1={paddingLeft}
          y1={paddingTop + innerHeight}
          x2={paddingLeft + innerWidth}
          y2={paddingTop + innerHeight}
          stroke="#e5e7eb"
          strokeWidth={1}
        />
        {/* Path */}
        <path d={pathD} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinecap="round" />
        {/* Points */}
        {points.map((p, index) => {
          const x = xForIndex(index);
          const y = yForValue(p[metricKey] ?? 0);
          const label = p.date.slice(5); // MM-DD
          return (
            <g key={p.date}>
              <circle cx={x} cy={y} r={3} fill="#1d4ed8" />
              <text
                x={x}
                y={paddingTop + innerHeight + 12}
                fontSize={9}
                textAnchor="middle"
                fill="#9ca3af"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};
